// 3rd party libraries
import React, { ReactElement, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// State management

// Components
import { CustomerPrice, Gallery, Input, Loader, Typography } from 'components';

// Helpers
import { getProductDescription } from 'common/helpers/v1';

import {
  CompatibleLabel,
  DiscontinuedLabel,
  OrderInputs,
  PartDataErrorContact,
  PartInfo,
  PriceContainer,
  QuantityContainer
} from 'pages/ProductDetailPage/PlaceOrder';
// Providers
import { useLanguage } from 'providers';

// Types
import { Asset, Machine, ResourceType } from 'types';
import { Product } from 'types/parts';

// Constants
import { JBTRoutes } from 'constants/routes';
import theme from 'themes';
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import breakpoint from 'constants/breakpoints';
import StockIndicator2 from 'components/StockIndicator/StockIndicator2';
import { ToastMsg } from 'common/components/Toast';
import {
  useDeleteSavedProductMutation,
  useGetSavedProductsQuery,
  useSavedProductMutation
} from 'api';

const TopLayout = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  @media (max-width: ${breakpoint.size.md}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const GalleryContainer = styled.div`
  width: 100%;
  @media (max-width: ${breakpoint.size.md}px) {
    max-width: 100%;
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const ProductDetailRootContainer = styled.div`
  padding: 1.25rem;
  .product-desc {
    margin: 0 0 2rem;
    padding: 0.75rem;
    font-size: 0.875rem;
    background-color: ${theme.colors.info300};
    color: ${theme.colors.primaryBlue5};
    border-radius: 0.5rem;
    width: fit-content;
  }
  .stock-view {
    justify-content: space-between;
    border-bottom: 1px solid ${theme.colors.tableborder};
    padding: 0.4rem 0;
    align-items: center;
  }
  @media (max-width: ${breakpoint.size.md}px) {
    flex-direction: column;
    align-items: center;
  }
`;

// Component properties
interface Props {
  product: Product;
  machine?: Machine;
  quantity?: number;
  handleQuantity?: (quantity: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addCartResult?: any;
}

export default function PartDetailsView({
  product,
  machine,
  quantity,
  handleQuantity,
  addCartResult
}: Props): ReactElement {
  const { t } = useTranslation(['fpns']);
  const { languageId } = useLanguage();
  const [savedProductInput] = useSavedProductMutation();
  const [deleteProduct] = useDeleteSavedProductMutation();
  const { data: savedProducts, refetch: refreshSavedProductsList } = useGetSavedProductsQuery();

  // Trigger analytic event when component mounts
  useEffect(() => {
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.VIEW_ITEM,
      label: product.id
    });
  }, [product.id]);

  // Make sure we only show contact information once, even if there are many errors in the data
  // We show contact info for the first error encountered, and not for the subsequent ones
  const showContact = {
    price: !!product.priceError,
    stock: !!(!product.priceError && product.stockError),
    leadtime: !!(!product.priceError && !product.stockError && product.leadTimeError)
  };
  const isSavedProduct = useMemo(() => {
    return savedProducts?.some((prod) => prod.productId === product.id);
  }, [savedProducts, product]);
  const getCompatibility = (product: Product) => {
    if (product.compatibility) {
      return (
        <>
          <CompatibleLabel>{t('compatible_with')}</CompatibleLabel>{' '}
          {product.compatibility?.length &&
            product.compatibility.map((m) => m.description).join(', ')}
        </>
      );
    } else if (machine) {
      <>
        <CompatibleLabel>{t('compatible_with')}</CompatibleLabel>{' '}
        {machine ? machine.description : '-'}
      </>;
    }
    return null;
  };
  const handleSave = () => {
    console.log('handleSave');
    savedProductInput([
      {
        productId: product.id,
        quantity: 1,
        priceUnit: product.priceUnit
      }
    ])
      .unwrap()
      .then(() => {
        ToastMsg({
          position: 'top-right',
          heading: 'Success',
          type: 'success',
          message: t('product_saved_successfully')
        });
        refreshSavedProductsList(); // required to update local saved list for Heart icon toggle
      })
      .catch((error) => {
        ToastMsg({
          heading: 'Error',
          type: 'error',
          position: 'top-right',
          message: error?.data?.detail
        });
      });
  };
  const handleDelete = () => {
    deleteProduct({
      productId: product.id,
      priceUnit: product.priceUnit
    })
      .unwrap()
      .then(() => {
        ToastMsg({
          position: 'top-right',
          heading: 'Success',
          type: 'success',
          message: product.description + t('saved_product_remove')
        });
      })
      .catch((error) => {
        console.warn('Delete saved product error: ', error);
        ToastMsg({
          position: 'top-right',
          heading: 'Error',
          message: t('saved_product_delete_error'),
          type: 'error'
        });
      });
  };
  return (
    <ProductDetailRootContainer>
      {addCartResult.isLoading && <Loader />}
      {!addCartResult.isLoading && (
        <>
          <Typography mb={'2rem'} className="product-desc" variant="h2">
            {getProductDescription(product, languageId)}
          </Typography>
          <TopLayout>
            <GalleryContainer>
              <Gallery
                images={
                  product?.assets && product.assets.length
                    ? (product?.assets as Asset[])
                        .filter((asset) => {
                          return asset.assetType === ResourceType.ProductImage;
                        })
                        .map((asset) => asset.url)
                    : ['/assets/placeholder/part.svg']
                }
              />
            </GalleryContainer>

            <PartInfo>
              <StockIndicator2
                purchasable={product}
                isSavedProduct={isSavedProduct}
                handleDelete={handleDelete}
                handleSave={handleSave}
              />

              {product.overview && <Typography mb="1.25rem">{product.overview}</Typography>}
              {(product.compatibility || machine) && (
                <Typography size="0.75rem" weight="bold" mb="0.5rem">
                  {getCompatibility(product)}
                </Typography>
              )}
              {product.discontinued && (
                <Typography size="0.75rem" mb="1.25rem">
                  <DiscontinuedLabel>{t('discontinued')}</DiscontinuedLabel>
                  {product.alternateProduct && (
                    <>
                      {' '}
                      {t('alternative')}:{' '}
                      <Link
                        to={`${JBTRoutes.partsProduct.replace(
                          ':productId',
                          product.alternateProduct.id
                        )}}`}
                      >
                        {product.alternateProduct?.description}
                      </Link>
                    </>
                  )}
                </Typography>
              )}

              {product.priceError && product.businessUnit ? (
                <PartDataErrorContact
                  priceError={product.priceError}
                  showContactInfo={showContact.price}
                  businessUnit={product.businessUnit}
                />
              ) : (
                <PriceContainer>
                  <CustomerPrice product={product} bold={true} />
                  <OrderInputs>
                    <QuantityContainer>
                      <Input
                        type="number"
                        label={
                          <Typography weight="semi-bold" mb={'.3rem'}>
                            {t('QTY', { ns: 'common' }) as string}
                          </Typography>
                        }
                        id="qty"
                        min={1}
                        variant={product.isPurchasable ? 'white' : 'disabled'}
                        value={quantity}
                        onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                          const rawQuantity =
                            evt.target.value.length < 1 ? String(0) : evt.target.value;
                          const newQuantity =
                            Math.max(parseInt(rawQuantity), 0) == 0
                              ? quantity
                              : Math.max(parseInt(rawQuantity), 0);
                          handleQuantity?.(newQuantity || 0);
                        }}
                      />
                    </QuantityContainer>
                  </OrderInputs>
                </PriceContainer>
              )}
            </PartInfo>
          </TopLayout>
        </>
      )}
    </ProductDetailRootContainer>
  );
}
